import { IExtendedAddress } from '../global/extendedAddress';
import { IOrganization } from '../global/organization';
import { IDestinationManager, IUser } from '../user/user';
import { IPagination, IPaginationParams } from '../global/pagination';
import { IPosition } from '../global/position';
import { ISortParams } from '../global/sort';

export interface ITransferCompany {
  id: number;
  organizationId: number;
  taxiLicenseFileId: number;
  chamberOfCommerceLicenseFileId: number;
  vatNumber: string;
  vatRegion: string;
  paymentTermsDays: number;
  postalAddress: IExtendedAddress;
  invoiceAddress: IExtendedAddress;
  websiteUrl: string;
  onboardingStatus: string;
  mainLanguage: string;
  spokenLanguages: string[];
  cocNumber: string;
  ibanCode: string;
  swiftCode: string;
  payoneerId: string;
  preferredPayoutMethod: string;
  managedByUserId: number;
  ibanAccountHolder: string;
  phone: string;
  invoicingFrequency: string;
  invoicingCurrencyCode: string;
  wheelchairAdapted: boolean;
  organization: IOrganization;
  user: IUser;
  destinationManagers: IDestinationManager[];
}

export interface IMeetingPoint {
  id: number;
  hubTenderId: number;
  title: string;
  description: string;
  mapFileId: number;
  mapFile: any;
  geographicLocation: IPosition;
  type?: string;
}

export interface IMeetingPointsEntityList {
  results: IMeetingPoint[];
  pagination: IPagination;
}

export interface ITransferCompanies {
  results: ITransferCompany[];
  pagination: IPagination;
}

export interface IFleet {
  id: number;
  name: string;
  transferCompanyId: number;
  active: boolean;
  acceptNewRides: boolean;
  lockedForComplaints: boolean;
  timeZoneId: string;
  availableTravelAddons: string[];
  availableVehicleCategories: string[];
  offerEmail: string;
  customerSupportInfo?: IFleetCustomerSupportinfo
}

export interface ISearchFleetsRequestParams extends IPaginationParams {
  dynamicSearch: string;
}

export interface ISearchFleet {
  id: number;
  transferCompanyId: number;
  name: string;
  transferCompanyName: string;
}

export enum FleetType {
  All = 'ALL',
  Specific = 'SPECIFIC',
}

export interface IComplaint {
  id: number;
  fleetId: number;
  journeyId: number;
  comment: string;
  status: string;
  created: string;
  expectedResolution: string;
}

export interface IComplaintParams extends IPaginationParams {
  fleetId: number;
  driverId: number;
  journeyId: number;
  status: string;
  createdFrom: string;
  createdTo: string;
}

export interface IFleetCustomerSupportinfo {
  phone: string;
  email: string;
  fullTimeSupport: boolean;
  openingHoursFrom: string;
  openingHoursTo: string;
  emergencyEmail: string;
  emergencyPhone: string;
}

export interface IHubTenderExpirationSetting {
  id: number;
  hubTenderId: number;
  days: string[];
  startTime: string;
  endTime: string;
  expirationMinutes: number;
}

export enum ComplaintStatusType {
  Resolved = 'RESOLVED',
  Open = 'OPEN',
}

export enum TranscoInvoicingFrequencyType {
  BiWeekly = 'BI_WEEKLY',
  Monthly = 'MONTHLY',
}

export const TranscoInvoicingFrequencyDisplayName = {
  [TranscoInvoicingFrequencyType.BiWeekly]: 'Bi-weekly',
  [TranscoInvoicingFrequencyType.Monthly]: 'Monthly',
};

export const ComplaintStatusTypeDisplayName = {
  [ComplaintStatusType.Resolved]: 'Resolved',
  [ComplaintStatusType.Open]: 'Open',
};

export interface IServiceAccountParams {
  name: string;
  emailAddress: string;
}

export interface IServiceAccount {
  userId?: number;
  password?: string;
  existingUser?: boolean;
}

export enum VATRegionType {
  Netherlands = 'NETHERLANDS',
  Europe = 'EUROPE',
  Other = 'OTHER',
}

export const VATRegionDisplayName = {
  [VATRegionType.Netherlands]: 'Netherlands',
  [VATRegionType.Europe]: 'Europe',
  [VATRegionType.Other]: 'Other',
};

export enum MeetingPointFileType {
  Map = 'MAP',
  Desk = 'DESK',
  Pickup = 'PICKUP',
  Other = 'OTHER',
}

export const MeetingPointFileTypeDisplayName = {
  [MeetingPointFileType.Map]: 'Map',
  [MeetingPointFileType.Desk]: 'Desk',
  [MeetingPointFileType.Pickup]: 'Pickup',
  [MeetingPointFileType.Other]: 'Other',
};

export interface IAssociatedDriversParams extends IPaginationParams, ISortParams {
  fleetId?: number;
  transferCompanyId?: number;
  name?: string;
  phone?: string;
}

export interface IAssociatedDriver {
  id: number;
  name: string;
  phone: string;
  transferCompanyId: string;
  fleetId: number;
  created: string;
}

export interface IAssignDriverToJourneyParams {
  journeyId?: number;
  driverId?: number;
  phoneNumber?: string;
  name?: string;
  sendSms?: boolean;
}

export interface IAssociatedDriversUpdateParams {
  id: number,
  name: string,
  phone: string,
}
